import React, { useState, useEffect } from "react";
import { Mail, Phone, LifeBuoy } from "lucide-react";
import axios from "axios";
import a1 from "../assets/bg.webp";
import { Helmet } from "react-helmet";

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    domain: "TechVisit",
  });

  const [otp, setOtp] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  /* ---------------- OTPless INIT ---------------- */
  useEffect(() => {
    if (window.OTPless) {
      window.OTPlessSignin = new window.OTPless(() => {});
    }
  }, []);

  /* ---------------- HANDLERS ---------------- */
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const requestOTP = () => {
    if (!formData.phone) {
      alert("Enter phone number");
      return;
    }

    setOtpRequested(true);

    window.OTPlessSignin.initiate({
      channel: "PHONE",
      phone: formData.phone,
      countryCode: "+91",
    });
  };

  const verifyOTP = () => {
    window.OTPlessSignin
      .verify({
        channel: "PHONE",
        phone: formData.phone,
        otp,
        countryCode: "+91",
      })
      .then((res) => {
        if (res.success) {
          setOtpVerified(true);
          setStatus("OTP verified successfully");
        } else {
          setStatus("Invalid OTP");
        }
      })
      .catch(() => setStatus("OTP verification failed"));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otpVerified) {
      alert("Please verify OTP first");
      return;
    }

    setLoading(true);
    setStatus("");

    try {
      await axios.post("https://api.ecrop.co/api/contact/send", {
        ...formData,
        timestamp: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
        }),
      });

      setStatus("Form submitted successfully");

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
        domain: "TechVisit",
      });

      setOtp("");
      setOtpRequested(false);
      setOtpVerified(false);
    } catch {
      setStatus("Form submission failed");
    }

    setLoading(false);
  };

  return (
    <>
     <Helmet>
        <title>Contact Drone Didi | Agri-Drone Support & Women Empowerment</title>

        <meta
          name="description"
          content="For any query, call us directly at +91 80972 834 44 or email us at mail@precisiongrow.co.in.  You can get in touch with us through our website too."
        />

        <link
          rel="canonical"
          href="https://dronedidi.co.in/contact"
        />
      </Helmet>
  
    <section className="relative">
      {/* ---------------- HERO + FORM ---------------- */}
      <div className="relative h-[600px] md:h-[700px]">
        <img src={a1} alt="Contact background" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="absolute inset-0 flex flex-col justify-center items-center px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
            Contact Us
          </h2>

          <p className="text-gray-200 text-center mb-8 max-w-2xl">
            We use an agile approach to test assumptions and connect with your audience.
          </p>

          <div className="bg-white -mb-52 rounded-xl shadow-lg p-6 md:p-12 w-full max-w-3xl">
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" required className="border rounded-lg p-3" />
              <input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" required className="border rounded-lg p-3" />
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Your email" required className="border rounded-lg p-3" />

              <div className="flex gap-2">
                <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number" required className="border rounded-lg p-3 w-full" />
                <button type="button" onClick={requestOTP} className="bg-green-900 text-white px-4 rounded-lg">
                  OTP
                </button>
              </div>

              {otpRequested && (
                <div className="flex gap-2 md:col-span-2">
                  <input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Enter OTP" className="border rounded-lg p-3 w-full" />
                  <button type="button" onClick={verifyOTP} className="bg-green-600 text-white px-4 rounded-lg">
                    Verify
                  </button>
                </div>
              )}

              <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Your message" required className="border rounded-lg p-3 md:col-span-2 h-32" />

              {status && <p className="md:col-span-2 text-xl text-green-900 text-center">"{status}"</p>}

              <button type="submit" disabled={!otpVerified || loading} className="md:col-span-2 bg-green-900 text-white py-3 rounded-lg hover:bg-green-700">
                {loading ? "Submitting..." : "Send message"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* SPACER */}
<div class="h-48 md:h-24"></div>
      {/* ---------------- BOTTOM INFO CARDS ---------------- */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow hover:bg-gradient-to-br hover:from-green-400 hover:to-green-800 hover:text-white transition">
          <Mail className="w-8 h-8 mb-3" />
          <h4 className="font-semibold mb-1">Email us</h4>
          <p className="text-sm">Email us for general queries.</p>
          <a href="mailto:hello@techvisit.com" className="mt-2 font-medium">
            hello@techvisit.com
          </a>
        </div>

        <div className="bg-green-200 rounded-xl p-6 flex flex-col items-center text-center shadow scale-105">
          <Phone className="w-8 h-8 mb-3" />
          <h4 className="font-semibold mb-1">Call us</h4>
          <p className="text-sm">We are always happy to help.</p>
          <a href="tel:+9164667865060" className="mt-2 font-medium">
            +91 64667 865060
          </a>
        </div>

        <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow hover:bg-gradient-to-br hover:from-green-400 hover:to-green-800 hover:text-white transition">
          <LifeBuoy className="w-8 h-8 mb-3" />
          <h4 className="font-semibold mb-1">Support</h4>
          <p className="text-sm">Need help? Visit support center.</p>
          <button className="mt-2 px-4 py-2 bg-green-900 text-white rounded-lg">
            Support Center
          </button>
        </div>
      </div>
    </section>
      </>
  );
};

export default Contact;
